import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { ScrollArea } from './ui/scroll-area';
import { 
  Receipt, Download, Trash2, CheckCircle, XCircle, Clock,
  FileText
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const InvoiceListModal = ({ open, onClose }) => {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (open) {
      fetchInvoices();
    }
  }, [open]);

  const fetchInvoices = async () => {
    try {
      const response = await axios.get(`${API}/invoices`);
      setInvoices(response.data);
    } catch (error) {
      toast.error('Kunne ikke hente fakturaer');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPdf = async (invoice) => {
    try {
      const response = await axios.get(`${API}/invoices/${invoice.id}/pdf`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `faktura_${invoice.invoice_number}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('PDF downloadet');
    } catch (error) {
      toast.error('Kunne ikke downloade PDF');
    }
  };

  const handleUpdateStatus = async (invoice, status) => {
    try {
      await axios.put(`${API}/invoices/${invoice.id}/status?status=${status}`);
      toast.success('Status opdateret');
      fetchInvoices();
    } catch (error) {
      toast.error('Kunne ikke opdatere status');
    }
  };

  const handleDelete = async (invoice) => {
    if (!window.confirm(`Er du sikker på du vil slette faktura ${invoice.invoice_number}?`)) {
      return;
    }
    try {
      await axios.delete(`${API}/invoices/${invoice.id}`);
      toast.success('Faktura slettet');
      fetchInvoices();
    } catch (error) {
      toast.error('Kunne ikke slette faktura');
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'paid':
        return <Badge className="bg-green-500">Betalt</Badge>;
      case 'cancelled':
        return <Badge variant="destructive">Annulleret</Badge>;
      default:
        return <Badge variant="secondary">Afventer</Badge>;
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh]" data-testid="invoice-list-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Receipt className="h-5 w-5" />
            Fakturaer
          </DialogTitle>
          <DialogDescription>
            Oversigt over alle oprettede fakturaer
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" />
          </div>
        ) : invoices.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            <Receipt className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>Ingen fakturaer oprettet endnu</p>
          </div>
        ) : (
          <ScrollArea className="h-[500px] pr-4">
            <div className="space-y-3">
              {invoices.map((invoice) => (
                <div
                  key={invoice.id}
                  className="p-4 rounded-lg border bg-card hover:shadow-md transition-shadow"
                  data-testid={`invoice-item-${invoice.invoice_number}`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-mono font-semibold">{invoice.invoice_number}</span>
                        {getStatusBadge(invoice.status)}
                      </div>
                      <p className="font-medium">{invoice.customer_name}</p>
                      <div className="text-sm text-muted-foreground space-y-1 mt-2">
                        <p>Arbejdskort: #{invoice.workcard_number}</p>
                        <p>Udstedt: {invoice.issue_date}</p>
                        <p>Betalingsfrist: {invoice.payment_due_date}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-xl font-bold">
                        {invoice.total.toLocaleString('da-DK')} kr
                      </div>
                      <p className="text-sm text-muted-foreground">
                        inkl. {invoice.vat_rate}% moms
                      </p>
                    </div>
                  </div>

                  {/* Line items preview */}
                  <div className="mt-3 pt-3 border-t">
                    <div className="text-sm text-muted-foreground">
                      {invoice.line_items.slice(0, 2).map((item, idx) => (
                        <p key={idx} className="truncate">
                          • {item.description} ({item.total} kr)
                        </p>
                      ))}
                      {invoice.line_items.length > 2 && (
                        <p>+ {invoice.line_items.length - 2} flere poster</p>
                      )}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2 mt-4 pt-3 border-t">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDownloadPdf(invoice)}
                      data-testid={`download-invoice-${invoice.invoice_number}`}
                    >
                      <Download className="h-4 w-4 mr-1" />
                      PDF
                    </Button>
                    
                    {invoice.status === 'pending' && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleUpdateStatus(invoice, 'paid')}
                        className="text-green-600 hover:text-green-700"
                        data-testid={`mark-paid-${invoice.invoice_number}`}
                      >
                        <CheckCircle className="h-4 w-4 mr-1" />
                        Marker Betalt
                      </Button>
                    )}
                    
                    {invoice.status === 'paid' && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleUpdateStatus(invoice, 'pending')}
                        className="text-yellow-600 hover:text-yellow-700"
                      >
                        <Clock className="h-4 w-4 mr-1" />
                        Marker Afventer
                      </Button>
                    )}
                    
                    <div className="flex-1" />
                    
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(invoice)}
                      className="text-destructive hover:text-destructive"
                      data-testid={`delete-invoice-${invoice.invoice_number}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </ScrollArea>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default InvoiceListModal;
